import React from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../content/SiteContext';
import { organizationSchema, businessSchema, websiteSchema } from '../lib/schema';
import { PageMeta, SectionHead, PackageCard, CtaBand, FaqList, Icon, Price, WhatsAppButton, AppButton, StoreBadge, CheckList } from '../components/ui';

const STEPS = [
  { icon: 'car', title: 'Choose your package', text: 'Pick an exterior wash, an inside-and-out clean or a full detail in the Quick Way app. Prices are shown up front.' },
  { icon: 'pin', title: 'Share your location and time', text: 'Drop a pin where the car is parked in Abu Dhabi or Dubai and pick a time that suits you. Home, tower car park or office.' },
  { icon: 'chat', title: 'We confirm and come to you', text: 'Our team confirms your slot and final price, then comes to your car. You do not need to drive anywhere.' }
];

const REASONS = [
  { icon: 'clock', title: 'No queue, no drive', text: 'Your car is washed where it is parked, so you can carry on with your day.' },
  { icon: 'tag', title: 'Clear package prices', text: 'AED 30, 45 or 65 per wash, with every inclusion listed. The final price is confirmed before we start.' },
  { icon: 'building', title: 'Homes, towers and offices', text: 'Villa driveways, residential car parks and office parking across Abu Dhabi and Dubai.' },
  { icon: 'calendar', title: 'One-off or monthly', text: 'Book a single wash, or choose a monthly plan with priority booking.' }
];

const PromoBanner = ({ promo }) => (
  <div className="promo-banner" role="region" aria-label="Current offer">
    <div className="container">
      {promo.badge && <span className="promo-banner__badge">{promo.badge}</span>}
      <strong>{promo.title}</strong>
      {promo.description && <span className="promo-banner__desc">{promo.description}</span>}
      <span className="promo-banner__save">
        {promo.discountType === 'percent' ? `Save ${promo.value}%` : `Save AED ${promo.value}`}
        {promo.end ? ` · ends ${promo.end}` : ''}
      </span>
      <Link to="/book" className="btn btn-primary btn-sm">Book now</Link>
    </div>
  </div>
);

const Home = () => {
  const { PACKAGES, PLANS, CITIES, FAQS, HOME_SECTIONS, PROMOTIONS } = useSite();

  // CMS homepage builder: home_section records control order + visibility.
  // Keys map to built-in sections; unknown/missing keys fall back to defaults.
  const conf = Object.fromEntries(HOME_SECTIONS.map((s) => [s.key, s]));
  const promo = PROMOTIONS[0];

  const sections = [
    ['hero', (
      <section className="hero hero--photo" key="hero">
        <div className="hero__bg" aria-hidden="true">
          <picture>
            <source srcSet="/img/hero-wash.webp" type="image/webp" />
            <img src="/img/hero-wash.jpg" alt="" width="1440" height="810" fetchpriority="high" decoding="async" />
          </picture>
        </div>
        <div className="container hero__inner">
          <div className="hero__copy">
            <p className="eyebrow eyebrow--light"><Icon name="pin" size={16} /> {CITIES.map((c) => c.name).join(' & ')}</p>
            <h1>Mobile car wash in {CITIES.map((c) => c.name).join(' & ')}, <span className="text-accent">at your door</span></h1>
            <p className="hero__lead">
              Quick Way washes and details your car at home, in your building car park or at the office.
              Order in the Quick Way app: pick a package from AED {PACKAGES[0]?.price ?? 30}, drop a pin and we come to you.
            </p>
            <div className="btn-row">
              <AppButton label="Get the app" className="btn btn-primary btn-lg" source="hero" />
              <WhatsAppButton text="Hi Quick Way, I would like to book a car wash." label="WhatsApp us" className="btn btn-ghost btn-lg" source="hero" />
            </div>
            <ul className="hero__facts">
              <li><Icon name="tag" size={18} /> From AED {PACKAGES[0]?.price ?? 30} per wash</li>
              <li><Icon name="home" size={18} /> Home, tower or office</li>
              <li><Icon name="calendar" size={18} /> One-off or monthly plans</li>
            </ul>
          </div>
          <div className="hero__visual" aria-hidden="true">
            <div className="phone">
              <picture>
                <source srcSet="/img/app-location.webp" type="image/webp" />
                <img src="/img/app-location.jpg" alt="" width="385" height="816" fetchpriority="high" />
              </picture>
            </div>
            <div className="hero__card hero__card--top">
              <Icon name="pin" size={18} /> <span>Wash at your location</span>
            </div>
            <div className="hero__card hero__card--bottom">
              <span className="hero__card-label">Deluxe Wash</span>
              <Price value={45} />
            </div>
          </div>
        </div>
      </section>
    )],
    ['steps', (
      <section className="section" id="how-it-works" key="steps">
        <div className="container">
          <SectionHead eyebrow="How it works" title="A doorstep car wash in three steps">
            Booking takes a couple of minutes. You stay where you are and the wash comes to you.
          </SectionHead>
          <ol className="steps">
            {STEPS.map((s, i) => (
              <li key={s.title} className="step">
                <span className="step__num">{i + 1}</span>
                <span className="icon-tile"><Icon name={s.icon} /></span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    )],
    ['packages', (
      <section className="section section--tint" id="packages" key="packages">
        <div className="container">
          <SectionHead eyebrow="Packages and prices" title="Car wash packages with clear prices">
            Every package is done at your location. Add extras such as odour removal or ceramic coating when you book.
          </SectionHead>
          <div className="grid grid-3">
            {PACKAGES.map((p) => <PackageCard key={p.id} pkg={p} />)}
          </div>
          <p className="center-note">
            <Link to="/pricing" className="link-arrow">Compare packages and extras <Icon name="arrow" size={16} /></Link>
          </p>
        </div>
      </section>
    )],
    ['areas', (
      <section className="section" key="areas">
        <div className="container">
          <SectionHead eyebrow="Service areas" title="Where we wash cars">
            We currently serve {CITIES.map((c) => c.name).join(' and ')}. Send your exact location when booking and we will confirm coverage.
          </SectionHead>
          <div className="grid grid-2">
            {CITIES.map((c) => (
              <Link key={c.id} to={c.path} className="city-card">
                <span className="icon-tile"><Icon name="pin" /></span>
                <h3>Mobile car wash in {c.name}</h3>
                <p>{(c.areas || []).slice(0, 5).join(', ')} and more.</p>
                <span className="link-arrow">See {c.name} coverage <Icon name="arrow" size={16} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    )],
    ['plans', (
      <section className="section section--dark" key="plans">
        <div className="container split">
          <div>
            <p className="eyebrow eyebrow--light">Monthly plans</p>
            <h2>Keep your car clean every week</h2>
            <p className="lead-light">
              Monthly plans bundle regular washes with priority booking.
              {PLANS.find((p) => p.featured) && ` The ${PLANS.find((p) => p.featured).name} gives you ${PLANS.find((p) => p.featured).washes} for AED ${PLANS.find((p) => p.featured).price}.`}
            </p>
            <Link to="/membership" className="btn btn-primary">Compare monthly plans</Link>
          </div>
          <ul className="plan-mini-list">
            {PLANS.map((p) => (
              <li key={p.id} className={p.featured ? 'is-featured' : ''}>
                <div>
                  <strong>{p.name}</strong>
                  <span>{p.washes}</span>
                </div>
                <Price value={p.price} suffix="/ month" />
              </li>
            ))}
          </ul>
        </div>
      </section>
    )],
    ['reasons', (
      <section className="section" key="reasons">
        <div className="container">
          <SectionHead eyebrow="Why Quick Way" title="Why book a car wash at home?" />
          <div className="grid grid-4">
            {REASONS.map((r) => (
              <div key={r.title} className="feature">
                <span className="icon-tile"><Icon name={r.icon} /></span>
                <h3>{r.title}</h3>
                <p>{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    )],
    ['corporate', (
      <section className="section section--tint" key="corporate">
        <div className="container split split--center">
          <div>
            <p className="eyebrow">For businesses</p>
            <h2>Corporate and fleet car washing</h2>
            <p>
              Keep company cars, pool vehicles and staff cars clean without taking them off the road.
              We schedule washes at your office or depot in {CITIES.map((c) => c.name).join(' and ')} and agree pricing based on your fleet size.
            </p>
            <Link to="/corporate-fleet-car-wash" className="btn btn-outline-dark">Request a fleet quote</Link>
          </div>
          <div className="info-panel">
            <span className="icon-tile icon-tile--lg"><Icon name="truck" size={32} /></span>
            <ul className="checklist checklist--check">
              {['Fleets from 5 vehicles', 'Washes at your premises', 'Scheduled visits', 'One point of contact'].map((t) => (
                <li key={t}><Icon name="check" size={18} />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    )],
    ['faq', (
      <section className="section" key="faq">
        <div className="container container--narrow">
          <SectionHead eyebrow="FAQ" title="Mobile car wash questions" />
          <FaqList items={FAQS.slice(0, 6)} />
          <p className="center-note"><Link to="/faq" className="link-arrow">More questions answered <Icon name="arrow" size={16} /></Link></p>
        </div>
      </section>
    )],
    ['app', (
      <section className="section section--tint" key="app">
        <div className="container split split--center">
          <div>
            <p className="eyebrow">The Quick Way app</p>
            <h2>The fastest way to order your wash</h2>
            <p>Drop a pin where your car is parked, choose a package and extras, and manage every booking in one place.</p>
            <CheckList items={['Pick your car\u2019s location on a map', 'See prices before you order', 'Manage and repeat your washes']} />
            <StoreBadge source="home_app_section" />
            <p><Link to="/car-wash-app" className="link-arrow">See how the car wash app works <Icon name="arrow" size={16} /></Link></p>
          </div>
          <div className="app-shot">
            <img src="/img/app-location.jpg" alt="Quick Way app screen for choosing your car wash location on a map" width="385" height="816" loading="lazy" />
          </div>
        </div>
      </section>
    )],
    ['cta', <CtaBand source="home" key="cta" />]
  ];

  return (
    <>
      <PageMeta
        title="Mobile Car Wash at Home | Abu Dhabi & Dubai | Quick Way"
        description="Mobile car wash at your home, building or office in Abu Dhabi and Dubai. Exterior AED 30, inside and out AED 45, detailing AED 65. Order in the app."
        path="/"
        jsonLd={[websiteSchema(), organizationSchema(), businessSchema()]}
      />
      {promo && <PromoBanner promo={promo} />}
      {sections
        .map(([k, el], i) => ({ k, el, enabled: conf[k] ? conf[k].enabled !== false : true, ord: conf[k]?.order ?? i * 10 }))
        .filter((s) => s.enabled)
        .sort((a, b) => a.ord - b.ord)
        .map((s) => s.el)}
    </>
  );
};

export default Home;
